import { useCallback } from 'react';

import { gql } from '@apollo/client';
import { useApolloClient, useMutation } from '@apollo/client/react';
import type {
  ApprovalDecision,
  BrainSignalDefinitionStatus,
  BrainSignalStatus,
} from '@this+that/models';

import type {
  BrainSignalDefinitionSaveInput,
  BrainSignalDefinitionView,
  BrainSignalDetailView,
  BrainSignalTemplateOverridesInput,
  BrainSignalView,
} from '../models/brainSignals';

const NETWORK_ONLY = 'network-only';

const BRAIN_SIGNAL_FIELDS = gql`
  fragment BrainSignalFields on BrainSignal {
    teamId
    signalId
    defId
    scopeEntityId
    scopeEntityType
    level
    status
    lastChangeAt
    summary
    citedAssertionIds
  }
`;

const BRAIN_SIGNAL_DEFINITION_FIELDS = gql`
  fragment BrainSignalDefinitionFields on BrainSignalDefinition {
    teamId
    defId
    status
    templateId
    active {
      name
      description
      lang
      scope {
        entityTypes
      }
      match {
        eventKinds
        categories
        predicates
        entityTypes
        entityIds
        areaKeys
        classifications
        sourceTypes
      }
      rules {
        kind
        category
        predicate
        knowledgeState
        min
        days
        delta
      }
      policy {
        cooldownHours
        decayDays
        closeOnResolution
        reviewBeforeAction
        maxEvaluationsPerDay
        minConfidence
        minDistinctSources
      }
      actions {
        notify {
          userIds
          roles
          channels
          minLevel
        }
      }
      semantic {
        condition
        depth
        levels {
          level
          description
        }
      }
    }
  }
`;

const GET_BRAIN_SIGNAL_DEFINITIONS = gql`
  query brainSignalDefinitions($teamId: ID!) {
    brainSignalDefinitions(teamId: $teamId) {
      ...BrainSignalDefinitionFields
    }
  }
  ${BRAIN_SIGNAL_DEFINITION_FIELDS}
`;

const GET_BRAIN_SIGNALS = gql`
  query brainSignals(
    $teamId: ID!
    $status: BrainSignalStatus!
    $limit: Int
  ) {
    brainSignals(teamId: $teamId, status: $status, limit: $limit) {
      signals {
        ...BrainSignalFields
      }
    }
  }
  ${BRAIN_SIGNAL_FIELDS}
`;

const GET_BRAIN_SIGNAL = gql`
  query brainSignal($teamId: ID!, $signalId: ID!) {
    brainSignal(teamId: $teamId, signalId: $signalId) {
      signal {
        ...BrainSignalFields
      }
      history {
        version
        at
        fromLevel
        toLevel
        engine
        rationale
        reasonCodes
        policyApplied
        costMicroUsd
        model
        escalationReasons
        eventRefs {
          eventId
        }
      }
    }
  }
  ${BRAIN_SIGNAL_FIELDS}
`;

const RESPOND_TO_BRAIN_SIGNAL_REVIEW = gql`
  mutation respondToBrainSignalReview(
    $teamId: ID!
    $signalId: ID!
    $version: Int!
    $decision: ApprovalDecision!
  ) {
    respondToBrainSignalReview(
      teamId: $teamId
      signalId: $signalId
      version: $version
      decision: $decision
    ) {
      ...BrainSignalFields
    }
  }
  ${BRAIN_SIGNAL_FIELDS}
`;

const ACTIVATE_BRAIN_SIGNAL_TEMPLATE = gql`
  mutation activateBrainSignalTemplate(
    $teamId: ID!
    $templateId: String!
    $overrides: BrainSignalTemplateOverridesInput!
  ) {
    activateBrainSignalTemplate(
      teamId: $teamId
      templateId: $templateId
      overrides: $overrides
    ) {
      ...BrainSignalDefinitionFields
    }
  }
  ${BRAIN_SIGNAL_DEFINITION_FIELDS}
`;

const SET_BRAIN_SIGNAL_DEFINITION_STATUS = gql`
  mutation setBrainSignalDefinitionStatus(
    $teamId: ID!
    $defId: ID!
    $status: BrainSignalDefinitionStatus!
  ) {
    setBrainSignalDefinitionStatus(
      teamId: $teamId
      defId: $defId
      status: $status
    ) {
      ...BrainSignalDefinitionFields
    }
  }
  ${BRAIN_SIGNAL_DEFINITION_FIELDS}
`;

const SAVE_BRAIN_SIGNAL_DEFINITION = gql`
  mutation saveBrainSignalDefinition(
    $teamId: ID!
    $input: BrainSignalDefinitionInput!
  ) {
    saveBrainSignalDefinition(teamId: $teamId, input: $input) {
      ...BrainSignalDefinitionFields
    }
  }
  ${BRAIN_SIGNAL_DEFINITION_FIELDS}
`;

const CLOSE_BRAIN_SIGNAL = gql`
  mutation closeBrainSignal($teamId: ID!, $signalId: ID!) {
    closeBrainSignal(teamId: $teamId, signalId: $signalId) {
      ...BrainSignalFields
    }
  }
  ${BRAIN_SIGNAL_FIELDS}
`;

interface BrainSignalDefinitionsData {
  brainSignalDefinitions: BrainSignalDefinitionView[];
}

interface BrainSignalsData {
  brainSignals: { signals: BrainSignalView[] };
}

interface BrainSignalData {
  brainSignal: BrainSignalDetailView | null;
}

interface ActivateBrainSignalTemplateData {
  activateBrainSignalTemplate: BrainSignalDefinitionView;
}

interface SetBrainSignalDefinitionStatusData {
  setBrainSignalDefinitionStatus: BrainSignalDefinitionView;
}

interface SaveBrainSignalDefinitionData {
  saveBrainSignalDefinition: BrainSignalDefinitionView;
}

interface CloseBrainSignalData {
  closeBrainSignal: BrainSignalView;
}

function requireData<TData>(
  result: { data?: TData | null; error?: unknown },
  operation: string,
): TData {
  if (result.error || !result.data) {
    throw result.error ?? new Error(`${operation} returned no data`);
  }
  return result.data;
}

export function useGetBrainSignalDefinitions() {
  const client = useApolloClient();

  const getBrainSignalDefinitions = useCallback(
    async (teamId: string): Promise<BrainSignalDefinitionView[]> => {
      const result = await client.query<BrainSignalDefinitionsData>({
        query: GET_BRAIN_SIGNAL_DEFINITIONS,
        variables: { teamId },
        fetchPolicy: NETWORK_ONLY,
      });
      return requireData(result, 'brainSignalDefinitions')
        .brainSignalDefinitions;
    },
    [client],
  );

  return { getBrainSignalDefinitions };
}

export function useGetBrainSignals() {
  const client = useApolloClient();

  const getBrainSignals = useCallback(
    async (
      teamId: string,
      status: BrainSignalStatus,
      limit: number,
    ): Promise<BrainSignalView[]> => {
      const result = await client.query<BrainSignalsData>({
        query: GET_BRAIN_SIGNALS,
        variables: { teamId, status, limit },
        fetchPolicy: NETWORK_ONLY,
      });
      return requireData(result, 'brainSignals').brainSignals.signals;
    },
    [client],
  );

  return { getBrainSignals };
}

export function useGetBrainSignal() {
  const client = useApolloClient();

  const getBrainSignal = useCallback(
    async (
      teamId: string,
      signalId: string,
    ): Promise<BrainSignalDetailView | null> => {
      const result = await client.query<BrainSignalData>({
        query: GET_BRAIN_SIGNAL,
        variables: { teamId, signalId },
        fetchPolicy: NETWORK_ONLY,
      });
      return requireData(result, 'brainSignal').brainSignal;
    },
    [client],
  );

  return { getBrainSignal };
}

export function useActivateBrainSignalTemplate() {
  const [mutate] = useMutation<ActivateBrainSignalTemplateData>(
    ACTIVATE_BRAIN_SIGNAL_TEMPLATE,
  );

  const activateBrainSignalTemplate = useCallback(
    async (
      teamId: string,
      templateId: string,
      overrides: BrainSignalTemplateOverridesInput,
    ): Promise<BrainSignalDefinitionView> => {
      const result = await mutate({
        variables: { teamId, templateId, overrides },
      });
      return requireData(result, 'activateBrainSignalTemplate')
        .activateBrainSignalTemplate;
    },
    [mutate],
  );

  return { activateBrainSignalTemplate };
}

export function useSetBrainSignalDefinitionStatus() {
  const [mutate] = useMutation<SetBrainSignalDefinitionStatusData>(
    SET_BRAIN_SIGNAL_DEFINITION_STATUS,
  );

  const setBrainSignalDefinitionStatus = useCallback(
    async (
      teamId: string,
      defId: string,
      status: BrainSignalDefinitionStatus,
    ): Promise<BrainSignalDefinitionView> => {
      const result = await mutate({ variables: { teamId, defId, status } });
      return requireData(result, 'setBrainSignalDefinitionStatus')
        .setBrainSignalDefinitionStatus;
    },
    [mutate],
  );

  return { setBrainSignalDefinitionStatus };
}

export function useSaveBrainSignalDefinition() {
  const [mutate] = useMutation<SaveBrainSignalDefinitionData>(
    SAVE_BRAIN_SIGNAL_DEFINITION,
  );

  const saveBrainSignalDefinition = useCallback(
    async (
      teamId: string,
      input: BrainSignalDefinitionSaveInput,
    ): Promise<BrainSignalDefinitionView> => {
      const result = await mutate({ variables: { teamId, input } });
      return requireData(result, 'saveBrainSignalDefinition')
        .saveBrainSignalDefinition;
    },
    [mutate],
  );

  return { saveBrainSignalDefinition };
}

export function useCloseBrainSignal() {
  const [mutate] = useMutation<CloseBrainSignalData>(CLOSE_BRAIN_SIGNAL);

  const closeBrainSignal = useCallback(
    async (teamId: string, signalId: string): Promise<BrainSignalView> => {
      const result = await mutate({ variables: { teamId, signalId } });
      return requireData(result, 'closeBrainSignal').closeBrainSignal;
    },
    [mutate],
  );

  return { closeBrainSignal };
}

interface RespondToBrainSignalReviewData {
  respondToBrainSignalReview: BrainSignalView;
}

export function useRespondToBrainSignalReviewService() {
  const [mutate] = useMutation<RespondToBrainSignalReviewData>(
    RESPOND_TO_BRAIN_SIGNAL_REVIEW,
  );

  const respondToBrainSignalReview = useCallback(
    async (
      teamId: string,
      signalId: string,
      version: number,
      decision: ApprovalDecision,
    ): Promise<BrainSignalView> => {
      const result = await mutate({
        variables: { teamId, signalId, version, decision },
      });
      return requireData(result, 'respondToBrainSignalReview')
        .respondToBrainSignalReview;
    },
    [mutate],
  );

  return { respondToBrainSignalReview };
}
